import { useEffect, useState, FormEvent } from 'react';
import type { Category } from '@lr/shared';
import { apiFetch } from '../../lib/api';

interface CategoryRow extends Category {
  items?: { count: number }[] | null;
}

export function AdminCategories() {
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    apiFetch<CategoryRow[]>('/api/admin/categories')
      .then(setRows)
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Categories</h1>
          <div className="sub">{rows.length} {rows.length === 1 ? 'category' : 'categories'}.</div>
        </div>
        <div className="actions">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Add category
          </button>
        </div>
      </div>

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">No categories yet</div>
            <div className="p">Categories let you group items (e.g. Outerwear, Shirts).</div>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Add category
            </button>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th style={{ width: 100 }}>Items</th>
                  <th style={{ width: 100 }}>Sort</th>
                  <th style={{ width: 100 }}>Active</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const count = c.items?.[0]?.count ?? 0;
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td className="id">{c.slug}</td>
                      <td className="num">{count}</td>
                      <td className="num">{c.sort_order}</td>
                      <td>
                        {c.is_active ? (
                          <span className="badge badge-good">Active</span>
                        ) : (
                          <span className="badge badge-neutral">Hidden</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-ghost"
                          style={{ color: count > 0 ? 'var(--a-muted)' : 'var(--a-danger)' }}
                          title={
                            count > 0
                              ? `Has ${count} item${count === 1 ? '' : 's'} — reassign first`
                              : 'Delete'
                          }
                          onClick={async () => {
                            if (count > 0) return;
                            if (!confirm(`Delete category "${c.name}"?`)) return;
                            try {
                              await apiFetch(`/api/admin/categories/${c.id}`, {
                                method: 'DELETE'
                              });
                              load();
                            } catch (e) {
                              alert(e instanceof Error ? e.message : 'Delete failed');
                            }
                          }}
                          disabled={count > 0}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          title="New category"
          endpoint="/api/admin/categories"
          onClose={(created) => {
            setShowCreate(false);
            if (created) load();
          }}
        />
      )}
    </>
  );
}

function CreateModal({
  title,
  endpoint,
  onClose
}: {
  title: string;
  endpoint: string;
  onClose: (created?: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const finalSlug = slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          name,
          slug: finalSlug,
          sort_order: Number(sortOrder) || 0
        })
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <form onSubmit={onSubmit} style={{ display: 'contents' }}>
          <div className="a-modal-head">
            <h2>{title}</h2>
            <button type="button" className="btn-ghost" onClick={() => onClose()}>
              ×
            </button>
          </div>
          <div className="a-modal-body">
            {error && <div className="a-alert a-alert-error">{error}</div>}
            <div className="a-form">
              <div className="a-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="a-field">
                <label>Slug</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="auto-generated"
                />
              </div>
              <div className="a-field">
                <label>Sort order</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
                <div className="help">Lower numbers appear first.</div>
              </div>
            </div>
          </div>
          <div className="a-modal-foot">
            <button type="button" className="btn-secondary" onClick={() => onClose()}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
